import React from 'react';
import { MapPin, Phone, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="border-t bg-white">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-start gap-12 px-4 py-8 md:grid-cols-3">
        {/* Logo & slogan */}
        <div>
          <div className="mb-2 flex items-center gap-3">
            <img
              src="/logos/ms4all_logo_with_tagline.svg"
              alt="MS4ALL Logo"
              className="sm:h-18 h-16 w-fit"
              width={180}
              height={64}
            />
          </div>
          <div className="mt-10 flex items-center gap-2">
            <span className="font-medium">{t('footer.followUs')} :</span>
            <a
              href="https://www.linkedin.com/company/ms4all/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="/logos/linkedin.png"
                alt="LinkedIn"
                className="h-6 w-auto object-contain"
                width={24}
                height={24}
                loading="lazy"
                decoding="async"
              />
            </a>
            <a
              href="https://www.youtube.com/@MS4ALL-uc2oz"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="/logos/YouTube_full-color_icon_(2017).svg.png"
                alt="YouTube"
                className="h-6 w-auto object-contain"
                width={24}
                height={24}
                loading="lazy"
                decoding="async"
              />
            </a>
          </div>
        </div>

        {/* Contact */}
        <div>
          <div className="mb-2 inline-block border-b-2 border-[#006AB5] font-bold">
            {t('footer.contactUs')}
          </div>
          <div className="mt-4 flex flex-col items-start space-y-3">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-gray-600" />
              <div className="text-sm">
                <div>1 Avenue du champ de Mars</div>
                <div>45100 Orléans, France</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-gray-600" />
              <span className="text-sm">+33 (0)6 48 00 29 48</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-gray-600" />
              <span className="text-sm">contact@ms4all.eu</span>
            </div>
          </div>
        </div>

        {/* Partners */}
        <div>
          <div className="mb-2 inline-block border-b-2 border-[#006AB5] font-bold">
            {t('footer.partners')}
          </div>
          <ul className="grid grid-cols-3 gap-6 2xl:grid-cols-4 2xl:gap-12">
            <li className="flex w-14 items-center justify-center">
              <a
                href="https://www.davincilabs.eu/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="/logos/da_vinci_labs_logo.webp"
                  alt="Da Vinci Labs"
                  className="w-full"
                  width={56}
                  height={56}
                  loading="lazy"
                  decoding="async"
                />
              </a>
            </li>
            <li className="flex w-12 items-center justify-center">
              <a
                href="https://www.tech-orleans.fr/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="/logos/technopole_orleans_logo.webp"
                  alt="Orléans Technopole"
                  width={48}
                  height={48}
                  loading="lazy"
                  decoding="async"
                />
              </a>
            </li>
            <li className="flex w-16 items-center justify-center">
              <a
                href="https://www.info.gouv.fr/grand-dossier/france-2030"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="/logos/france_2030_logo.webp"
                  alt="France 2030"
                  width={64}
                  height={64}
                  loading="lazy"
                  decoding="async"
                />
              </a>
            </li>
            <li className="flex w-20 items-center justify-center">
              <a
                href="https://www.bpifrance.fr/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="/logos/bpifrance_logo.webp"
                  alt="BPI France"
                  width={80}
                  height={80}
                  loading="lazy"
                  decoding="async"
                />
              </a>
            </li>
            <li className="flex w-20 items-center justify-center">
              <a
                href="https://www.poleaquanova.fr/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="/logos/aquanova_logo.webp"
                  alt="AQUANOVA"
                  width={80}
                  height={80}
                  loading="lazy"
                  decoding="async"
                />
              </a>
            </li>
            <li className="flex w-20 items-center justify-center">
              <a
                href="https://www.polymeris.fr/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="/logos/polymeris_logo.webp"
                  alt="Polymeris"
                  width={80}
                  height={80}
                  loading="lazy"
                  decoding="async"
                />
              </a>
            </li>
            <li className="flex w-20 items-center justify-center">
              <a
                href="https://www.safran-group.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="/logos/safran_logo.webp"
                  alt="Safran"
                  width={80}
                  height={80}
                  loading="lazy"
                  decoding="async"
                />
              </a>
            </li>
          </ul>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-2 border-t px-4 py-2 text-xs text-gray-500 md:flex-row">
        <div className="flex flex-col items-center gap-2 md:flex-row md:gap-4">
          <div>{t('footer.rights', { year: currentYear })}</div>
        </div>
        <div className="mt-2 flex gap-4 md:mt-0">
          <Link
            to="/legal-notice"
            className="transition-colors hover:text-[#006AB5]"
          >
            {t('footer.legalNotice')}
          </Link>
          <a
            href="/documents/251201GTCms4all.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="transition-colors hover:text-[#006AB5]"
          >
            {t('footer.GTC')}
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
